import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { changeRole } from "@/utils/api";

interface ChangeRoleModalProps {
  opened: boolean;
  onClose: () => void;
  teamMember: {
    userId: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    status: string;
  } | null;
  onSuccess?: () => void;
}

const ChangeRoleModal = ({
  opened,
  onClose,
  teamMember,
  onSuccess,
}: ChangeRoleModalProps): React.ReactElement => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const roleOptions = [
    {
      name: "Team Lead",
      value: "team_lead",
      description:
        "Can view and access all clients in the business. Has elevated permissions compared to regular team members.",
    },
    {
      name: "Team Member",
      value: "team_member",
      description:
        "Can only view and access clients specifically assigned to them.",
    },
  ];

  // Reset selected role when modal opens/closes or member changes
  useEffect(() => {
    if (opened && teamMember) {
      // Set current role as default selection
      setSelectedRole(teamMember.role);
    } else {
      setSelectedRole(null);
    }
  }, [opened, teamMember]);

  const handleSave = async (): Promise<void> => {
    if (!teamMember || !selectedRole) return;

    // Don't allow changing to owner role
    if (selectedRole.toLowerCase() === "owner") {
      showNotification({
        title: "Error",
        message: "Cannot change member role to Owner.",
        color: "red",
      });
      return;
    }

    setIsLoading(true);

    try {
      await changeRole({
        userId: teamMember.userId,
        role: selectedRole,
      });

      showNotification({
        title: "Success",
        message: `Successfully changed role to ${roleOptions.find((r) => r.value === selectedRole)?.name || selectedRole}.`,
        color: "green",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error changing role:", error);
      showNotification({
        title: "Error",
        message: "Failed to change team member role. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderRoleOptions = roleOptions.map((option) => (
    <Radio.Card radius="md" value={option.value} key={option.value}>
      <Group wrap="nowrap" align="flex-start" p="xs">
        <Radio.Indicator />
        <div>
          <Text fw={500}>{option.name}</Text>
          <Text c="dimmed" size="sm">
            {option.description}
          </Text>
        </div>
      </Group>
    </Radio.Card>
  ));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Change Team Member Role</Text>}
      size="lg"
      radius={"md"}
      padding={"lg"}
    >
      <Stack gap="md">
        <Group gap="xs">
          <Text c="dimmed" size="sm">
            Team Member:{" "}
            <strong style={{ color: "#005fa3" }}>
              {teamMember?.firstName} {teamMember?.lastName}
            </strong>
          </Text>
        </Group>

        <Box>
          <Text c="dimmed" size="sm">
            Current Role:{" "}
            <strong>
              {roleOptions.find((r) => r.value === teamMember?.role)?.name ||
                teamMember?.role}
            </strong>
          </Text>
        </Box>

        <div>
          <Text fw={500}>Select New Role</Text>
          <Text c="dimmed" size="sm">
            Choose the role that best fits this team member's responsibilities
          </Text>
        </div>

        <Radio.Group
          mb={"md"}
          value={selectedRole}
          onChange={setSelectedRole}
          label="Available roles"
          description="Select a new role for this team member"
        >
          <Stack pt="sm" gap="xs">
            {renderRoleOptions}
          </Stack>
        </Radio.Group>
      </Stack>

      <Flex justify="flex-end" gap="sm">
        <Button variant="subtle" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            selectedRole === null ||
            selectedRole === teamMember?.role ||
            isLoading
          }
          loading={isLoading}
        >
          Change Role
        </Button>
      </Flex>
    </Modal>
  );
};

export default ChangeRoleModal;
